import { useIndexDB } from "@/contexts/IndexDB/useIndexDB";
import { useRecordingDuration } from "@/hooks/useRecordingDuration";
import isValidTime from "@/utils/isValidTime";
import { formatTimeToHms } from "@/utils/formatTime";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RowTextStyled } from "../RecordingListTable/RecordingListTable.styled";

interface RecordingDurationCellProps {
  recordingId: string;
  staticDuration: string;
  isInProgress: boolean;
}

/**
 * Displays recording duration with priority order:
 * 1. Live duration from broadcasts if actively recording
 * 2. Duration from IndexedDB if recording exists there and is not in progress
 * 3. Static duration (fallback)
 */
const RecordingDurationCell: FC<RecordingDurationCellProps> = ({ recordingId, staticDuration, isInProgress }) => {
  const { t } = useTranslation();
  const { allRecordingData, isOnIndexDB } = useIndexDB();
  const isLocalRecording = isOnIndexDB(recordingId);

  // Get local recording snapshot from IndexedDB when available.
  const localRecording = useMemo(() => {
    const recordingInDB = allRecordingData.find((record) => record.recordingUUID === recordingId);
    return recordingInDB || null;
  }, [allRecordingData, recordingId]);

  const indexDBDuration = localRecording?.duration || null;
  const isEffectivelyInProgress = isLocalRecording ? localRecording?.status === 8 : isInProgress;
  const { formattedDuration } = useRecordingDuration(isEffectivelyInProgress ? recordingId : undefined);

  // Local recordings should always use IndexedDB duration unless they are currently in progress.
  const displayDuration =
    isEffectivelyInProgress && isLocalRecording
      ? formattedDuration || indexDBDuration || staticDuration
      : isLocalRecording
        ? indexDBDuration || staticDuration
        : staticDuration;

  const hmsUnits = { hours: t("recordsList.hoursUnit"), minutes: t("recordsList.minutesUnit"), seconds: t("recordsList.secondsUnit") };

  return <RowTextStyled>{isValidTime(displayDuration) ? formatTimeToHms(displayDuration, hmsUnits) : "-"}</RowTextStyled>;
};

export default RecordingDurationCell;
